import {LibraryAddRounded} from "@mui/icons-material";
import {Button, Checkbox, Chip, Grid, ListItemText, MenuItem, TextField, useTheme} from "@mui/material";
import {useLocation, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import {AgGridReact} from "ag-grid-react";
import '../../style/SalesMarketing.css';
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams} from "ag-grid-enterprise";
import {useAgGridLocale, useHasScope} from "../../../../config/globalConfig";
import dayjs from "dayjs";
import AdvancedFilter from "../../component/AdvancedFilter";
import * as Yup from 'yup';
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {
    ALPHANUMERIC_REG_EXP,
    BUSINESS_AGREEMENT_STATUS_DISMISSED,
    BUSINESS_AGREEMENT_STATUS_DRAFT,
    BUSINESS_AGREEMENT_STATUS_READY,
    INTEGER_REG_EXP
} from "../../component/Constants";
import {useProductCodeActions} from "../../service/useProductCodeActions";
import {DatePicker} from "@mui/x-date-pickers";
import {useBusinessAgreementActions} from "../../service/useBusinessAgreementActions";
import Stack from "@mui/material/Stack";
import BusinessAgreementDetailModal from "./modals/BusinessAgreementDetailModal";
import {useStatusValuesBusinessAgreement} from "../../component/useStatusValuesBusinessAgreement";
import EmptyState from "../../../../util/component/EmptyState";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import ActionButtonWithTooltip, {ActionTypes} from "../../component/ActionButtonWithTooltip";

const BusinessAgreementListPage = () => {
    const {globalData} = useContext(GlobalContext);
    const {t} = useTranslation(['salesmarketing', 'translation']);
    const {t: tg} = useTranslation('translation');
    const navigate = useNavigate();
    const {getBusinessAgreementList} = useBusinessAgreementActions();
    const {listSegment} = useProductCodeActions();
    const {getStatusColorAgreement, getStatusLabelAgreement} = useStatusValuesBusinessAgreement();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const businessAgreementCode = queryParams.get('businessAgreementCode');

    const [selectedBusinessAgreement, setSelectedBusinessAgreement] = useState<any>(null);
    const [openDetail, setOpenDetail] = useState(false);
    const filterDefaultValues = {
        legalEntity: globalData?.user?.legalEntities?.map((le: any) => le.name),
        dealer: globalData?.user?.dealers?.map((d: any) => d.name),
        segment: "",
        status: [],
        customerName: "",
        personName: "",
        personSurname: "",
        customerId: "",
        name: "",
        description: "",
        startDate: null,
        endDate: null,
        businessCode: businessAgreementCode ? businessAgreementCode : "",
        cig: ''
    };
    const customDefaultValues = {
        legalEntity: globalData?.user?.legalEntities?.map((le: any) => le.name),
        dealer: globalData?.user?.dealers?.map((d: any) => d.name),
        segment: "",
        status: [],
        customerName: "",
        personName: "",
        personSurname: "",
        customerId: "",
        name: "",
        description: "",
        startDate: null,
        endDate: null,
        businessCode: "",
        cig: ''
    };
    const [filters, setFilters] = useState<any>(filterDefaultValues);
    const [searchInput, setSearchInput] = useState("");
    const agGridLocale = useAgGridLocale();
    const gridRef = useRef<AgGridReact>(null);
    const [segments, setSegments] = useState<any[]>([]);
    const hasScopes = useHasScope;

    const theme = useTheme();
    const accentColor = theme.palette.accent.main;

    useEffect(() => {
        listSegment().then(res => {
            setSegments(res);
        })
    }, [])

    const columns: ColDef[] = [
        {
            field: "tenantId",
            headerName: t('salesmarketing:businessAgreement.listColumns.legalEntityField'),
            hide: globalData?.user?.legalEntities?.length <= 1,
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return globalData?.user?.legalEntities.find((le: any) => le.name === params.data.tenantId)?.name
            },
            tooltipValueGetter: (params: any) => {
                return globalData?.user?.legalEntities.find((le: any) => le.name === params.data.tenantId)?.name
            }
        },
        {
            field: "dealerId",
            headerName: t('salesmarketing:businessAgreement.listColumns.dealerField'),
            hide: globalData?.user?.dealers?.length <= 1,
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return globalData?.user?.dealers.find((le: any) => le.name === params.data.dealerId)?.name
            },
            tooltipValueGetter: (params: any) => {
                return globalData?.user?.dealers.find((le: any) => le.name === params.data.dealerId)?.name
            }
        },
        {
            field: "name",
            headerName: t('salesmarketing:businessAgreement.listColumns.name'),
            pinned: 'left',
            lockPosition: 'left',
            lockPinned: true,
            unSortIcon: true
        },
        {
            field: "description",
            headerName: t('salesmarketing:businessAgreement.listColumns.description'),
            tooltipField: "description",
            pinned: 'left',
            lockPosition: 'left',
            lockPinned: true,
            unSortIcon: true
        },
        {
            field: "businessCode",
            headerName: t('businessAgreement.filtersModal.businessCode'),
            unSortIcon: true
        },
        {
            field: "customerName",
            headerName: t('salesmarketing:businessAgreement.listColumns.customerName'),
            unSortIcon: true
        },
        {
            field: "personName",
            headerName: t('salesmarketing:businessAgreement.listColumns.personName'),
            unSortIcon: true
        },
        {
            field: "personSurname",
            headerName: t('salesmarketing:businessAgreement.listColumns.personSurname'),
            unSortIcon: true
        },
        {
            field: "customerId",
            headerName: t('salesmarketing:businessAgreement.listColumns.customerId'),
            unSortIcon: true
        },
        {
            field: "segmentCode",
            headerName: t('salesmarketing:businessAgreement.listColumns.segment'),
            unSortIcon: true,
            valueGetter: (params: any) => params.data.segmentCode && segments.length > 0 ? segments.find(s => s.segmentCode === params.data.segmentCode)?.segmentName : ""
        },
        {
            field: "configStartDate",
            headerName: t('salesmarketing:businessAgreement.listColumns.startDate'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
            unSortIcon: true
        },
        {
            field: "configEndDate",
            headerName: t('salesmarketing:businessAgreement.listColumns.endDate'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
            unSortIcon: true
        },
        {
            field: "status",
            maxWidth: 160,
            headerName: t('salesmarketing:businessAgreement.listColumns.status'),
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            cellClass: "grid-cell-center",
            headerClass: "grid-header-center",
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return <Chip label={getStatusLabelAgreement(params.data.status)}
                             sx={{backgroundColor: getStatusColorAgreement(params.data.status)}}/>
            }
        },
        {
            field: "_actions",
            maxWidth: 160,
            headerName: t('salesmarketing:businessAgreement.listColumns.action'),
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            sortable: false,
            cellRenderer: (params: any) => {
                return (
                    <Stack direction={"row"} justifyContent={"center"}>
                        <ActionButtonWithTooltip type={ActionTypes.edit} callback={() => {
                            window.open(`/salesmarketing/businessagreementupdate/${params.data.id}`, "_blank" );
                        }} auth={'SCOPE_SM_UPDATE_BUSINESS_AGREEMENT'}/>
                        <ActionButtonWithTooltip type={ActionTypes.details} callback={() => {
                            setOpenDetail(true);
                            setSelectedBusinessAgreement(params.data);
                        }} auth={'SCOPE_SM_VIEW_BUSINESS_AGREEMENT_DETAIL'}/>
                    </Stack>
                );
            }
        }
    ];

    const statuses = [
        {
            value: BUSINESS_AGREEMENT_STATUS_DRAFT,
            label: getStatusLabelAgreement(BUSINESS_AGREEMENT_STATUS_DRAFT),
            color: getStatusColorAgreement(BUSINESS_AGREEMENT_STATUS_DRAFT)
        },
        {
            value: BUSINESS_AGREEMENT_STATUS_READY,
            label: getStatusLabelAgreement(BUSINESS_AGREEMENT_STATUS_READY),
            color: getStatusColorAgreement(BUSINESS_AGREEMENT_STATUS_READY)
        },
        {
            value: BUSINESS_AGREEMENT_STATUS_DISMISSED,
            label: getStatusLabelAgreement(BUSINESS_AGREEMENT_STATUS_DISMISSED),
            color: getStatusColorAgreement(BUSINESS_AGREEMENT_STATUS_DISMISSED)
        },

    ];

    function createDatasource(filters: any, api: GridApi) {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let filterTemp = {...filters};
                filterTemp.offset = params.request.startRow ? params.request.startRow : 0;
                filterTemp.limit = (params.request.endRow ? params.request.endRow : 10) - filterTemp.offset;
                filterTemp.sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                filterTemp.order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;
                filterTemp.search = searchInput;
                if(businessAgreementCode){
                    filterTemp.businessCode = businessAgreementCode;
                }
                let data = await getBusinessAgreementList(filterTemp);

                params.success({
                    rowData: data?.content,
                    rowCount: data?.content.length
                });

                if (data.content.length === 0) {
                    api.showNoRowsOverlay();
                } else {
                    api.hideOverlay();
                }
            }
        };
    }


    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        rowModelType: 'serverSide',
        pagination: true,
        paginationPageSize: 10,
        cacheBlockSize: 10,
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        columnDefs: columns,
        serverSideInfiniteScroll: true,
        localeText: agGridLocale,
        noRowsOverlayComponentParams: {title: t('salesmarketing:businessAgreement.noRows')}
    };

    useEffect(() => {
        gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
    }, [filters]);

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const validationObj = {
        legalEntity: Yup.array().min(1, tg('validation.selectAtLeastOne')),
        status: Yup.array(),
        segment: Yup.string().nullable(),
        startDate: Yup.date().nullable()
            .test("testAgreementStartDate", t('salesmarketing:businessAgreement.datesCheck'), (value, context) => {
                return (!value || !context.parent.endDate || value <= context.parent.endDate)
            })
            .typeError(t('salesmarketing:businessAgreement.invalidDate')),
        endDate: Yup.date().nullable()
            .test("testAgreementEndDate", t('salesmarketing:businessAgreement.datesCheck'), (value, context) => {
                return (!value || !context.parent.startDate || value >= context.parent.startDate)
            })
            .typeError(t('salesmarketing:businessAgreement.invalidDate')),
        name: Yup.string().nullable(),
        description: Yup.string().nullable(),
        businessCode: Yup.string().nullable(),
        cig: Yup.string().nullable()
    }
    const renderStatus = (field: any) => (
        <TextField
            fullWidth
            label={t('salesmarketing:businessAgreement.filtersModal.statusField')}
            select
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) => selected.map((s: any) => statuses.find(ss => ss.value === s)!.label).join(', ')
            }}
            value={field.value}
            onChange={field.onChange}
        >
            {statuses?.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                    <Checkbox checked={field.value.indexOf(status.value) > -1}/>
                    <span style={{
                        border: "1px solid " + status.color,
                        backgroundColor: status.color,
                        borderRadius: "50%",
                        display: "inline-block",
                        height: "14px",
                        width: "14px",
                        marginRight: "5px"
                    }}/>
                    <ListItemText primary={status.label}/>
                </MenuItem>
            ))}
        </TextField>
    )

    const renderSegment = (field: any) => (
        <TextField
            fullWidth
            label={t('salesmarketing:businessAgreement.filtersModal.segmentField')}
            select
            value={field.value}
            onChange={field.onChange}
        >
            <MenuItem key={""} value={""}/>
            {segments?.map((s: any) => (
                <MenuItem key={s.segmentCode} value={s.segmentCode}>
                    {s.segmentName}
                </MenuItem>
            ))}
        </TextField>
    )

    const renderCustomerName = (field: any) => (
        <TextField
            fullWidth
            label={t('salesmarketing:businessAgreement.filtersModal.customerNameField')}
            value={field.value}
            onChange={field.onChange}
        />
    )
    const renderPersonName = (field: any) => (
        <TextField
            fullWidth
            label={t('salesmarketing:businessAgreement.filtersModal.personNameField')}
            value={field.value}
            onChange={field.onChange}
        />
    )
    const renderPersonLastName = (field: any) => (
        <TextField
            fullWidth
            label={t('salesmarketing:businessAgreement.filtersModal.personSurnameField')}
            value={field.value}
            onChange={field.onChange}
        />
    )

    const renderCustomerId = (field: any) => (
        <TextField
            fullWidth
            label={t('salesmarketing:businessAgreement.filtersModal.customerIdField')}
            value={field.value}
            onChange={(e) => {
                if (e.target.value === "" || INTEGER_REG_EXP.test(e.target.value)) {
                    field.onChange(e);
                }
            }}
        />
    )

    const renderBusinessCode = (field: any) => (
        <TextField
            fullWidth
            label={t('businessAgreement.filtersModal.businessCode')}
            value={field.value}
            onChange={(e) => {
                if (e.target.value === "" || (ALPHANUMERIC_REG_EXP.test(e.target.value) && e.target.value.length < 11)) {
                    field.onChange(e);
                }
            }}
        />
    )

    const renderAgreementName = (field: any) => (
        <TextField
            fullWidth
            label={t('salesmarketing:businessAgreement.filtersModal.agreementNameField')}
            value={field.value}
            onChange={field.onChange}
        />
    )

    const renderAgreementDescription = (field: any) => (
        <TextField
            fullWidth
            label={t('salesmarketing:businessAgreement.filtersModal.agreementDescriptionField')}
            value={field.value}
            onChange={field.onChange}
        />
    )

    const renderAgreementStartDate = (field: any, error: any, formState: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('salesmarketing:businessAgreement.filtersModal.agreementStartDateField')}
            value={field.value}
            onChange={field.onChange}
            maxDate={values["endDate"] ? values["endDate"] : ""}
            renderInput={(params) =>
                <TextField {...params} fullWidth
                           id={"agreementStartDate"}
                           error={error !== undefined}
                           helperText={error ? error.message : ""}
                />}
            inputFormat={"L"}
        />
    )

    const renderAgreementEndDate = (field: any, error: any, formState: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('salesmarketing:businessAgreement.filtersModal.agreementEndDateField')}
            value={field.value}
            onChange={field.onChange}
            minDate={values["startDate"] ? values["startDate"] : ""}
            renderInput={(params) =>
                <TextField {...params} fullWidth
                           id={"agreementEndDate"}
                           error={error !== undefined}
                           helperText={error ? error.message : ""}
                />}
            inputFormat={"L"}
        />
    )

    const renderCig = (field: any) => (
        <TextField
            fullWidth
            label={t('businessAgreement.cig')}
            value={field.value}
            onChange={(e) => {
                if (e.target.value === "" || ALPHANUMERIC_REG_EXP.test(e.target.value) ) {
                    field.onChange(e);
                }
            }}
        />
    );

    const fields = {
        status: renderStatus,
        customerName: renderCustomerName,
        personName: renderPersonName,
        personSurname: renderPersonLastName,
        customerId: renderCustomerId,
        segment: renderSegment,
        name: renderAgreementName,
        description: renderAgreementDescription,
        startDate: renderAgreementStartDate,
        endDate: renderAgreementEndDate,
        businessCode: renderBusinessCode,
        cig: renderCig
    }

    const toolbar = {
        renderActions: () => hasScopes('SCOPE_SM_CREATE_BUSINESS_AGREEMENT') ?
            <Button fullWidth size="small" onClick={() => {
                navigate('/salesmarketing/businessagreementcreate')
            }}>
                <LibraryAddRounded style={{paddingRight: "10px"}}/>
                {t('salesmarketing:businessAgreement.buttons.newBusinessAgreement')}
            </Button> : <></>
    }

    const reset = () => {
        setFilters(customDefaultValues);
        if (businessAgreementCode) {
            queryParams.delete('businessAgreementCode');
            const newUrl = `${location.pathname}`;
            navigate(newUrl);
        }
    };


    return (
        <>
            <SectionHeader title={t('salesmarketing:businessAgreement.listHeader.title')}
                           subtitle={t('salesmarketing:businessAgreement.listHeader.subTitle')}/>

            <AdvancedFilter validationObj={validationObj} fields={fields} defaultValues={filterDefaultValues}
                            customDefaultValues={customDefaultValues}
                            searchInput={searchInput} setSearchInput={setSearchInput} filters={filters}
                            setFilters={setFilters}  onReset={reset}/>

            <Grid container>
                <Grid item xs={12} sx={{padding: "16px"}}>
                    {segments.length > 0 ? <AgGridCustomTable
                            onGridReady={(params) => {
                                params.api.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
                            }}
                            noRowsOverlayComponent={noRowsOverlayComponent}
                            gridOptions={gridOptions} gridRef={gridRef} toolbar={toolbar}/>
                        :
                        <EmptyState title={t('salesmarketing:businessAgreement.noValues')}/>}
                </Grid>
            </Grid>
            {selectedBusinessAgreement &&
                <BusinessAgreementDetailModal key={selectedBusinessAgreement.id} open={openDetail}
                                              handleClose={() => setOpenDetail(false)}
                                              businessAgreement={selectedBusinessAgreement}/>}
        </>
    );
}

export default BusinessAgreementListPage;